"use client";

import React, { useState, useEffect } from "react";
import { format, parseISO, setHours, setMinutes, isValid } from "date-fns";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date/DatePicker";
import TimePickerComponent from "@/components/ui/date/TimePicker";

interface DateTimePickerProps {
  value?: Date;
  onChange: (date?: Date) => void;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({ value, onChange }) => {
  const [date, setDate] = useState<Date | undefined>(value);
  const [time, setTime] = useState(
    value
      ? `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`
      : "00:00"
  );

  useEffect(() => {
    setDate(value);
    setTime(
      value
        ? `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`
        : "00:00"
    );
  }, [value]);

  const handleDateChange = (dateString: string) => {
    const selectedDate = parseISO(dateString);
    if (isValid(selectedDate)) {
      const [hours, minutes] = time.split(":").map(Number);
      const newDate = setMinutes(setHours(selectedDate, hours), minutes);
      setDate(newDate);
      onChange(newDate);
    } else {
      setDate(undefined);
      onChange(undefined);
    }
  };

  const handleTimeChange = (newTime: string) => {
    setTime(newTime);
    if (date) {
      const [hours, minutes] = newTime.split(":").map(Number);
      const newDate = setMinutes(setHours(date, hours), minutes);
      setDate(newDate);
      onChange(newDate);
    }
  };

  return (
    <div className="space-y-2">
      <DatePicker
        value={date ? format(date, "yyyy-MM-dd") : ""}
        onChange={handleDateChange}
        placeholder="Select date..."
      />
      <div className="p-2 border rounded-md">
        <p className="text-sm font-medium mb-2">Time</p>
        <TimePickerComponent value={time} onChange={handleTimeChange} />
      </div>
    </div>
  );
};
